import { useParams, useLocation } from 'react-router-dom';
import { Spacer, TextTitle } from '@pypestream/design-system';
import { StudioPageProps } from '../hooks/use-pro-studio-routes';
import { WithLoadingState } from '../router';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { getChannelsFromPypes } from '../utils';
import { VoiceChannelSettingsForm } from '../components';
import { VoiceChannelFormValues } from '../components/voice-channel-settings-form/voice-channel-settings-form';

type VoiceChannelSettingsPageProps = StudioPageProps;

const VoiceChannelSettingsPage: React.FC<
  VoiceChannelSettingsPageProps
> = () => {
  const { channelId } = useParams();
  const location = useLocation();
  const isUpdating = !!channelId && !location.pathname.includes('/create');

  const { botPypes } = useProStudioCtxSelector((ctx) => ({
    botPypes: ctx.botPypes || [],
  }));

  const selectedChannel = isUpdating
    ? getChannelsFromPypes(botPypes).find(
        ({ id, type }) => id === channelId && type === 'twilio_voice'
      )
    : null;

  const initialValues: VoiceChannelFormValues = selectedChannel
    ? {
        botName: selectedChannel?.name || '',
        language: selectedChannel?.twilioVoiceConfig?.language || 'en-US',
        env: selectedChannel?.env || 'sandbox',
        ttsProvider:
          selectedChannel?.twilioVoiceConfig?.tts_provider || 'ElevenLabs',
        speechModel: selectedChannel?.twilioVoiceConfig?.speech_model || 'long',
        transcriptionProvider:
          selectedChannel?.twilioVoiceConfig?.transcription_provider ||
          'Google',
        elevenlabsAudioModel:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_audio_model || '',
        elevenlabsVoiceId:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_voice_id || '',
        elevenlabsSimilarity:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_similarity || 0,
        elevenlabsSpeed:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_speed || 0,
        elevenlabsStability:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_stability || 0,
        elevenlabsVoicePreviewUrl: '',
        elevenlabsVoiceMetadata:
          selectedChannel?.twilioVoiceConfig?.elevenlabs_voice_metadata,
        accountSid: selectedChannel?.twilioVoiceConfig?.account_sid || '',
        authToken: selectedChannel?.twilioVoiceConfig?.auth_token || '',
        phoneNumber: selectedChannel?.twilioVoiceConfig?.phone_number || '',
        webhookUrl:
          selectedChannel?.twilioVoiceConfig?.init_call_webhook_url || '',
      }
    : {
        botName: '',
        language: 'en-US',
        env: 'sandbox',
        ttsProvider: 'ElevenLabs',
        speechModel: 'long',
        transcriptionProvider: 'Google',
        elevenlabsAudioModel: '',
        elevenlabsVoiceId: '',
        elevenlabsSimilarity: 0.6,
        elevenlabsSpeed: 0.8,
        elevenlabsStability: 0.8,
        elevenlabsVoicePreviewUrl: '',
        elevenlabsVoiceMetadata: undefined,
        accountSid: '',
        authToken: '',
        phoneNumber: '',
        webhookUrl: '',
      };

  return (
    <>
      <TextTitle test-id="ps-voice-channel-settings-title" size="small">
        {isUpdating ? 'Channel Settings' : 'Create Voice Channel'}
      </TextTitle>
      <Spacer size="large" />
      <VoiceChannelSettingsForm
        type={isUpdating ? 'update' : 'create'}
        initialValues={initialValues}
        channelId={channelId}
      />
    </>
  );
};

export const VoiceChannelSettings = WithLoadingState(VoiceChannelSettingsPage);
